import {Component, inject, OnInit, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {Router} from '@angular/router';
import {AuthenticationService} from '../../../_auth/authentication.service';
import {lockoutRouteForSession} from '../../../_auth/gate-lockout-route';
import {ContactLinkHttpService} from '../../../_auth/contact/contact-link-http.service';
import {
  channelDisplayName,
  channelUnavailableMessage,
  ContactChannelInfo,
  ContactLink,
  ContactLinkPurpose,
  ContactLinkPurposeValue
} from '../../../_auth/contact/contact-link.models';
import {describeContactLinkError} from '../../../_auth/contact/contact-link-errors';
import {GlobalAlertService} from '../../../_services/global-alert.service';
import {AlertLevel} from '../../../enums/alert-level';
import {ContactLinkWizardComponent} from '../contact-linking/contact-link-wizard.component';

/** Which channel the wizard is open for, and why. Null while the page is just a list. */
interface ActiveWizard {
  readonly channel: string;
  readonly purpose: ContactLinkPurposeValue;
}

/**
 * „Meine Verknüpfungen" — the employee's self-view of their linked contact channels (#558).
 *
 * The read-only half is the same pattern as `EmployeeSubmissionsComponent`: the backend scopes
 * `GET /auth/contact-link/mine` to the caller, so there is no id to pass and nothing to filter here.
 *
 * What makes this page worth having rather than a line in the profile is the **„Geteilt mit" column**. An
 * account-level link is one channel identity shared with as many employers as the employee has consented to,
 * and that consent is the whole privacy story of the feature. The only place it is legible is here, so it is
 * listed per employer with the date it was given — a consent nobody can see afterwards is not much of a
 * consent.
 *
 * Three actions, in decreasing frequency:
 *
 * * **Erneut bestätigen** — a re-verification, run through the same {@link ContactLinkWizardComponent} the
 *   lockout screen uses. Offered always, not only when due: proving a link early is harmless and is what
 *   somebody who has just changed phones will look for.
 * * **Jetzt verknüpfen** — a channel the server offers that this account has not linked. Voluntary here (a
 *   *required* one would have restricted the session and never let it reach this page), and unavailable
 *   channels are shown as unavailable rather than hidden, so „warum kann ich das nicht?" has an answer.
 * * **Verknüpfung aufheben** — behind an inline confirmation that says the consequence plainly: if an employer
 *   requires the channel, the session is gated again at the next check. That sentence is the point of the
 *   confirmation; „Sind Sie sicher?" would tell nobody anything.
 */
@Component({
  selector: 'app-employee-contact-links',
  standalone: true,
  imports: [DatePipe, ContactLinkWizardComponent],
  templateUrl: './employee-contact-links.component.html'
})
export class EmployeeContactLinksComponent implements OnInit {
  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly links = signal<ContactLink[]>([]);
  /** The channel catalogue, so an unlinked-but-offered channel can be listed. */
  readonly channels = signal<ContactChannelInfo[]>([]);
  /** The link whose unlink confirmation is open, or null. */
  readonly confirmingUnlink = signal<string | null>(null);
  /** An unlink is in flight — keyed by link id so only that row's button is busy. */
  readonly unlinking = signal<string | null>(null);
  /** The wizard currently open, or null. */
  readonly wizard = signal<ActiveWizard | null>(null);

  protected readonly Purpose = ContactLinkPurpose;

  private readonly contacts = inject(ContactLinkHttpService);
  private readonly alerts = inject(GlobalAlertService);
  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);

  /** Whether the session's company is a demo company (#934); the wizard then shows why it cannot link. */
  protected get demoCompany(): boolean {
    return this.auth.isDemoCompany === true;
  }

  ngOnInit(): void {
    this.load();
  }

  /**
   * Re-mint the session after a change this page made to the gate's inputs, and swap the token in.
   *
   * The panel's warning strip and every gate guard read the TOKEN, not the server. A re-verification that
   * just succeeded, or a link that was just withdrawn, leaves the token saying what it said before — so
   * „Verknüpfung ausstehend" stayed up after a confirmed re-check on the first manual smoke, and a withdrawn
   * required link would let the session carry on until its next check. Same rule as the forms revise screen:
   * reissue, apply, and if the fresh claims restrict, go to the screen that lifts it. Best-effort — a failed
   * reissue is logged and the next live refresh catches up.
   */
  private refreshSession(): void {
    this.contacts.reissue().subscribe({
      next: token => {
        this.auth.applyReissuedSession(token);
        const lockout = lockoutRouteForSession(this.auth);
        if (lockout) this.router.navigate(['/' + lockout]).then();
      },
      error: err => console.warn('[contacts] session reissue failed', err)
    });
  }

  /**
   * Channels the server offers that are not linked yet.
   *
   * Derived rather than requested: the status call already carries both halves, and a second endpoint for
   * "the difference between these two lists" would be a second thing to keep in step.
   */
  get linkableChannels(): ContactChannelInfo[] {
    const linked = new Set(this.links().map(l => l.channel));
    return this.channels().filter(c => !linked.has(c.channel));
  }

  label(channel: string): string {
    return channelDisplayName(channel, this.channels());
  }

  /**
   * Why an offered channel cannot be linked — the same sentence the lockout screen uses, from the same helper.
   *
   * Worth stating because the two screens reach it from opposite directions (a catalogue entry here, a
   * restricted-with-nothing-to-do state there) and would otherwise word the identical fact differently. A
   * `botNotConfigured` reason in particular has to say the same thing in both places, or one of them sends
   * the reader to a manager the other has just told them cannot help.
   */
  unavailableMessage(channel: ContactChannelInfo): string {
    return channelUnavailableMessage(channel.displayName || this.label(channel.channel),
      channel.unavailableReason);
  }

  /**
   * Load the list.
   *
   * Two calls, and the second one's failure is deliberately NOT fatal: `/auth/contact-gate/status` is what
   * carries the channel catalogue, and a page that shows the employee's existing links is useful even when the
   * "what else could I link" half is unavailable. Losing the whole page over the optional half would be the
   * same mistake #502 fixed on the forms screen, one size down.
   */
  load(): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.confirmingUnlink.set(null);

    this.contacts.mine().subscribe({
      next: links => {
        this.links.set(links);
        this.loading.set(false);
        this.contacts.status().subscribe({
          next: status => this.channels.set(status.channels),
          error: () => this.channels.set([])
        });
      },
      error: () => {
        this.loading.set(false);
        this.errorMessage.set('Ihre Verknüpfungen konnten nicht geladen werden.');
      }
    });
  }

  /** Start a voluntary link of a channel that is offered and not yet linked. */
  linkChannel(channel: ContactChannelInfo): void {
    if (!channel.available) return;
    this.wizard.set({channel: channel.channel, purpose: ContactLinkPurpose.Link});
  }

  /** Re-prove an existing link is live. */
  reverify(link: ContactLink): void {
    this.wizard.set({channel: link.channel, purpose: ContactLinkPurpose.Reverify});
  }

  /** The wizard finished (re-mint + re-read) or was merely closed (nothing moved). */
  closeWizard(reload: boolean): void {
    this.wizard.set(null);
    if (!reload) return;
    this.refreshSession();
    this.load();
  }

  /** Open / close the inline unlink confirmation. */
  askUnlink(linkId: string): void {
    this.confirmingUnlink.set(this.confirmingUnlink() === linkId ? null : linkId);
  }

  /** Drop the link. The gate re-evaluates on its next check, which the confirmation says out loud. */
  confirmUnlink(link: ContactLink): void {
    if (this.unlinking()) return;
    this.unlinking.set(link.linkId);

    this.contacts.unlink(link.linkId).subscribe({
      next: () => {
        this.unlinking.set(null);
        this.confirmingUnlink.set(null);
        this.alerts.createAndShow('Verknüpfung aufgehoben',
          `${this.label(link.channel)} ist nicht mehr mit Ihrem Konto verknüpft.`, AlertLevel.success, 3000);
        this.refreshSession();
        this.load();
      },
      error: err => {
        this.unlinking.set(null);
        this.alerts.createAndShow('Fehler', describeContactLinkError(
          err, 'Die Verknüpfung konnte nicht aufgehoben werden.'), AlertLevel.error, 4000);
      }
    });
  }
}
